// Layer 3 — cursor-based projections for the Advisory flow landing.
// Stage milestones are parsed once from seeds; each replay tick is a lookup.

import type { AnalysisReplay } from '@/shared/contracts/analytics/v1';
import type {
  Case,
  FailureMode,
  FMEAUpdateReview,
  RecommendedAction,
} from '@/versions/v1/schema/case';
import type { DriverTreeSeed, PricedDriverTree } from '@/versions/v1/schema/driver-tree';
import type { ExcursionEvent } from '@/versions/v1/schema/spc';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { TagMapping } from '@/versions/v1/schema/tag-mapping';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import {
  caseStageAt,
  zincMarkerStageAt,
  type CaseStageMilestones,
  type CaseStageTimeline,
} from '@/versions/v1/shared/caseStage';
import type { PipelineCellRead, PipelineStageId } from './PipelineStrip.transform';
import { PIPELINE_STAGES } from './PipelineStrip.transform';
import type { InvestigationRunRow } from './InvestigationRun.transform';
import {
  addMinutes,
  clockOnProductionDay,
  diffMinutes,
  formatDayMonth,
  formatElapsedMinutes,
  shiftOf,
} from '@/versions/v1/shared/clock.derive';
import { CASE_OUTCOMES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { roleShortLabel } from '@/versions/v1/shared/org';
import { excursions } from '@/versions/v1/shared/SpcEngine';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { seriesAt } from '@/versions/v1/shared/series.derive';
import { matchedRuleIdForCase, ruleOutcome } from '@/versions/v1/shared/RuleSetEngine';
import type { Diagnosis, SignalRead } from '@/versions/v1/shared/DiagnosisEngine';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { formatCurrencyExact, formatCurrencyStandard, assertUsd } from '@/versions/v1/shared/money';
import { oneDecimalPercent } from './AgentInvestigation.transform';
import { stopPairRead } from './StopAction.transform';
import { fmeaCasePhase } from './FmeaLearning.transform';
import { variableIdForCase } from '@/versions/v1/shared/threads';
import { ruleLabel } from './AnalysisRun.transform';
import {
  driverIssueResolvedAtCursor,
  openCountedAmountPerDay,
  pricedDriverTree,
  openCaseExposurePerDay,
  totalExposurePerDay,
} from './DriverTree/Scene.transform';

export const ZINC_REPLAY_CASE_ID = 'case-zn-rgh-ph';
/** The one replay fixture on this landing; a case prices on it only if it names it. */
export const ZINC_REPLAY_FIXTURE = 'zinc-rougher';
export const ZINC_REPLAY_START: SeriesTime = '2026-06-07T00:00';
export const ZINC_REPLAY_END: SeriesTime = '2026-06-08T11:30';
export const REPLAY_DURATION_MS = 33_000;
export const REPLAY_DWELL_MS = 1_500;
export const REPLAY_STEP_MINUTES = 2;
const NO_ANALYSIS_FRAMES: readonly AnalysisReplay[number][] = [];
export type PipelineReplays = Readonly<Record<string, readonly AnalysisReplay[number][]>>;

export function caseReplayFrames(caseItem: Case, source: PipelineReplays | readonly AnalysisReplay[number][]): readonly AnalysisReplay[number][] {
  if (Array.isArray(source)) return caseItem.analysisRun?.fixture === ZINC_REPLAY_FIXTURE ? source : NO_ANALYSIS_FRAMES;
  return caseItem.analysisRun ? (source as PipelineReplays)[caseItem.analysisRun.fixture] ?? NO_ANALYSIS_FRAMES : NO_ANALYSIS_FRAMES;
}

export function analysisFramesForPipeline(
  zincFixtureFrames: readonly AnalysisReplay[number][] | undefined,
): readonly AnalysisReplay[number][] {
  return zincFixtureFrames ?? NO_ANALYSIS_FRAMES;
}

function wallClockMinute(stamp: SeriesTime): number {
  const [date, time] = stamp.split('T');
  const [year, month, day] = (date ?? '').split('-').map(Number);
  const [hour, minute] = (time ?? '').split(':').map(Number);
  return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, hour ?? 0, minute ?? 0) / 60_000;
}

export function flowClockOffset(cursor: SeriesTime): number {
  return Math.max(0, wallClockMinute(cursor) - wallClockMinute(ZINC_REPLAY_START));
}

export function flowClockAtOffset(offset: number): SeriesTime {
  const at = new Date((wallClockMinute(ZINC_REPLAY_START) + Math.max(0, offset)) * 60_000);
  return `${at.getUTCFullYear()}-${String(at.getUTCMonth() + 1).padStart(2, '0')}-${String(at.getUTCDate()).padStart(2, '0')}T${String(at.getUTCHours()).padStart(2, '0')}:${String(at.getUTCMinutes()).padStart(2, '0')}`;
}

export function formatFlowClock(cursor: SeriesTime): string {
  const [date, time] = cursor.split('T');
  const [, month, day] = (date ?? '').split('-').map(Number);
  const [hour, minute] = (time ?? '').split(':').map(Number);
  const monthLabel = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][(month ?? 1) - 1];
  const shiftLabel = shiftOf(cursor).kind === 'day' ? 'Day shift' : 'Night shift';
  return `${String(day ?? 1).padStart(2, '0')} ${monthLabel} · ${shiftLabel} · ${String(hour ?? 0).padStart(2, '0')}:${String(minute ?? 0).padStart(2, '0')}`;
}

function monthIndex(month: string): number {
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    .indexOf(month);
}

/** Parse authored case/review/action clocks without relying on host timezone. */
export function flowStamp(stamp: string, cursor: SeriesTime): SeriesTime | null {
  if (stamp.includes('T')) return stamp.slice(0, 16) as SeriesTime;
  if (/^\d{2}:\d{2}$/.test(stamp)) return clockOnProductionDay(stamp, cursor);
  const withDay = stamp.match(/^(\d{1,2}) ([A-Za-z]{3}) (\d{2}:\d{2})$/);
  if (withDay) {
    const month = monthIndex(withDay[2]!);
    if (month < 0) return null;
    return `${cursor.slice(0, 4)}-${String(month + 1).padStart(2, '0')}-${withDay[1]!.padStart(2, '0')}T${withDay[3]}`;
  }
  const dateOnly = stamp.match(/^(\d{1,2}) ([A-Za-z]{3})$/);
  if (dateOnly) {
    const month = monthIndex(dateOnly[2]!);
    if (month < 0) return null;
    return `${cursor.slice(0, 4)}-${String(month + 1).padStart(2, '0')}-${dateOnly[1]!.padStart(2, '0')}T00:00`;
  }
  return null;
}

export function analysisFrameAtClock(
  frames: readonly AnalysisReplay[number][],
  cursor: SeriesTime,
): AnalysisReplay[number] | undefined {
  let current: AnalysisReplay[number] | undefined;
  for (const frame of frames) {
    const at = frame.at.slice(0, 16) as SeriesTime;
    if (at > cursor) break;
    current = frame;
  }
  return current;
}

function firstReached(
  candidates: readonly (string | undefined)[],
  raisedAt: SeriesTime | null,
  fallbackCursor = DEFAULT_CURSOR,
): SeriesTime | null {
  if (!raisedAt) return null;
  const reached = candidates
    .flatMap((stamp) => {
      if (!stamp) return [];
      const parsed = flowStamp(stamp, fallbackCursor);
      return parsed && parsed >= raisedAt ? [parsed] : [];
    })
    .sort();
  return reached[0] ?? null;
}

/** Seed timestamps become a stable stage timetable; state labels are ignored. */
export function buildCaseStageTimeline(
  cases: readonly Case[],
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
  analysisFrames: PipelineReplays | readonly AnalysisReplay[number][] = [],
): CaseStageTimeline {
  const outcomeIdByCase = new Map(CASE_OUTCOMES.map((outcome) => [outcome.caseId, outcome.id]));
  const rows = new Map<string, CaseStageMilestones>();

  for (const caseItem of cases) {
    const raisedAt = flowStamp(caseItem.raisedAt, DEFAULT_CURSOR);
    const outcomeId = outcomeIdByCase.get(caseItem.id);
    const caseReviews = reviews.filter((review) => review.caseOutcomeId === outcomeId);
    const caseActions = actions.filter((action) => action.caseId === caseItem.id);
    rows.set(caseItem.id, {
      signalAt: null,
      excursionAt: null,
      raisedAt,
      diagnosisAt: firstReached([
        caseItem.diagnosedAt,
        ...caseReviews.map((review) => review.reviewedAt),
      ], raisedAt),
      actionAt: firstReached(caseActions.flatMap((action) => [
        action.proposedAt,
        action.decision?.at,
        action.status === 'done' ? action.implementedAt : undefined,
      ]), raisedAt),
      valueAt: firstReached([caseItem.resolvedAt], raisedAt),
      learnAt: firstReached(caseReviews.map((review) =>
        review.approvalStatus === 'approved' ? review.approvedAt : undefined,
      ), raisedAt),
    });
  }

  for (const caseItem of cases) {
    const timeline = rows.get(caseItem.id)!;
    const frames = caseReplayFrames(caseItem, analysisFrames);
    if (!frames.length) continue;
    const role = caseItem.analysisRun?.primarySignalRole;
    const signals = frames.flatMap((frame) => (frame.result.spc_signals ?? [])
      .filter((signal) => signal.fired && signal.signal_role === role));
    // A fixed guard starts at the evidence's crossing, not the replay segment.
    const fixed = signals.filter((signal) => signal.limits?.band_high != null);
    const primary = fixed.length ? fixed : signals;
    const signalAt = primary.map((signal) => flowStamp(
      signal.limits?.band_high != null ? signal.evidence_points[0]?.event_time ?? signal.first_event_time! : signal.segment_start!, DEFAULT_CURSOR,
    )).filter((at): at is SeriesTime => at !== null).sort()[0] ?? null;
    const excursionAt = primary.map((signal) => signal.first_event_time ? flowStamp(signal.first_event_time, DEFAULT_CURSOR) : null)
      .filter((at): at is SeriesTime => at !== null).sort()[0] ?? null;
    if (!caseItem.analysisRun?.milestonesFromFrames) {
      rows.set(caseItem.id, { ...timeline, signalAt, excursionAt });
      continue;
    }
    const diagnosisAt = frames.find((frame) => frame.result.one_off_stop_pair && frame.result.diagnosis?.candidates.length)?.at.slice(0, 16) as SeriesTime | undefined;
    // Frames establish the condition; the seeded case registration stays at 03:15.
    const visibleDiagnosisAt = diagnosisAt && timeline.raisedAt && diagnosisAt < timeline.raisedAt ? timeline.raisedAt : diagnosisAt ?? null;
    rows.set(caseItem.id, { ...timeline, signalAt, excursionAt, diagnosisAt: visibleDiagnosisAt, actionAt: visibleDiagnosisAt });
  }
  return rows;
}

const CASE_STAGE_ORDER: readonly PipelineStageId[] = ['cases', 'diagnosis', 'action', 'value', 'learn'];

function milestonesForCase(
  caseItem: Case,
  timelines: CaseStageTimeline,
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
): CaseStageMilestones {
  return timelines.get(caseItem.id) ?? buildCaseStageTimeline([caseItem], actions, reviews).get(caseItem.id)!;
}

export type PipelineMarkerEvent = {
  at: SeriesTime;
  stage: PipelineStageId;
  dwellMs: number;
};

/** Stage changes the replay schedule must hold long enough to show on glass. */
export function pipelineMarkerEvents(
  timeline: CaseStageMilestones | undefined,
): PipelineMarkerEvent[] {
  if (!timeline) return [];
  const candidates: Array<{ at: SeriesTime | null; stage: PipelineStageId }> = [
    { at: timeline.signalAt, stage: 'signals' },
    { at: timeline.excursionAt, stage: 'excursions' },
    { at: timeline.raisedAt ? addMinutes(timeline.raisedAt, 1) : null, stage: 'cases' },
    { at: timeline.diagnosisAt, stage: 'diagnosis' },
    { at: timeline.actionAt, stage: 'action' },
    { at: timeline.valueAt, stage: 'value' },
    { at: timeline.learnAt, stage: 'learn' },
  ];
  const sorted = candidates
    .filter((event): event is { at: SeriesTime; stage: PipelineStageId } => Boolean(event.at))
    .sort((left, right) => left.at.localeCompare(right.at));
  const result: PipelineMarkerEvent[] = [];
  for (const event of sorted) {
    if (result.at(-1)?.stage === event.stage) continue;
    result.push({ ...event, dwellMs: REPLAY_DWELL_MS });
  }
  return result;
}

export type FlowReplaySchedule = {
  startOffset: number;
  endOffset: number;
  travelDurationMs: number;
  durationMs: number;
  dwells: readonly (PipelineMarkerEvent & { offset: number })[];
};

export function buildFlowReplaySchedule(
  startCursor: SeriesTime,
  timeline: CaseStageMilestones | undefined,
): FlowReplaySchedule {
  const startOffset = Math.min(flowClockOffset(ZINC_REPLAY_END), flowClockOffset(startCursor));
  const endOffset = flowClockOffset(ZINC_REPLAY_END);
  const distance = Math.max(0, endOffset - startOffset);
  const travelDurationMs = REPLAY_DURATION_MS * (distance / Math.max(1, endOffset));
  const dwells = pipelineMarkerEvents(timeline)
    .filter((event) => event.at > startCursor && event.at <= ZINC_REPLAY_END)
    .map((event) => ({ ...event, offset: flowClockOffset(event.at) }));
  return {
    startOffset,
    endOffset,
    travelDurationMs,
    durationMs: travelDurationMs + dwells.reduce((total, event) => total + event.dwellMs, 0),
    dwells,
  };
}

export function replayOffsetAtElapsed(schedule: FlowReplaySchedule, elapsedMs: number): number {
  const elapsed = Math.max(0, elapsedMs);
  const distance = schedule.endOffset - schedule.startOffset;
  if (distance <= 0 || schedule.travelDurationMs <= 0) return schedule.endOffset;
  let heldMs = 0;
  for (const event of schedule.dwells) {
    const travelToEvent = schedule.travelDurationMs *
      ((event.offset - schedule.startOffset) / distance);
    const holdStarts = travelToEvent + heldMs;
    if (elapsed < holdStarts) {
      return schedule.startOffset + distance *
        (Math.max(0, elapsed - heldMs) / schedule.travelDurationMs);
    }
    if (elapsed < holdStarts + event.dwellMs) return event.offset;
    heldMs += event.dwellMs;
  }
  const movingElapsed = Math.min(schedule.travelDurationMs, elapsed - heldMs);
  return schedule.startOffset + distance * (movingElapsed / schedule.travelDurationMs);
}

/** Quantise replay travel without allowing a held event to move the cursor back. */
export function replayStepOffset(rawOffset: number, lastStepOffset: number): number {
  return Math.max(lastStepOffset, Math.floor(rawOffset / REPLAY_STEP_MINUTES) * REPLAY_STEP_MINUTES);
}

export function recoveryCasesForFlow(
  cases: readonly Case[],
  diagnosis?: Diagnosis,
): Case[] {
  return cases.filter((caseItem) => {
    const ruleId = matchedRuleIdForCase(caseItem, diagnosis);
    const rule = ruleId ? ruleById(ruleId) : undefined;
    const outcome = rule ? ruleOutcome(rule) : undefined;
    return outcome === 'recovery' || outcome === 'both';
  });
}

export type FlowStageCounts = Readonly<Record<PipelineStageId, number>>;
export type FlowCaseRow = {
  caseItem: Case;
  stage: PipelineStageId;
  stageIndex: number;
  age: string;
  actor: string;
  dailyLossPerDay: number | null;
  money: string | null;
  moneyComparison: string;
  feedLabel: string;
  actionStatus: 'awaiting approval' | 'in the field' | 'stop planned' | 'FMEA proposal · Senior met';
  /**
   * D17 addendum: the owner case code when this row prices a contributor share
   * that already sits inside another row's counted amount. The money cell reads
   * muted and the comparison names the owner, so the row is never summed twice.
   */
  containedWithin?: string;
};
export type ShiftFeedRow = {
  at: SeriesTime;
  timeLabel: string;
  actor: 'Engine' | 'Agent' | 'Human';
  detail: string;
  caseId: string;
  repeatCount: number;
};
export type FlowLearnRow = {
  id: string;
  at: SeriesTime;
  summary: string;
  detail: string;
  source: 'review' | 'case-approval';
  approvalCaseId?: string;
  /** The row's own code (`FMR-0006`, `ZN-RGH-PH`) and the seat or person who
   *  approved it at `at` (polish.P28c r1: the learned row names its approver). */
  code?: string;
  approvedBy?: string;
};
export type FlowCapturedRow = { caseItem: Case; at: SeriesTime; dailyLossPerDay: number | null; feedLabel: string };

/** A case's own counted share: the driver-tree figure its row prices and ranks on. */
export type AnalysisCaseValue = { comparison: string; money?: string; oneOff?: boolean };

export type FlowLandingRead = {
  fixtureSignals: readonly FlowFixtureSignal[];
  fixtureExcursions: readonly FlowFixtureExcursion[];
  markers: readonly { caseId: string; label: string; stage: PipelineStageId; offset: number; stack: number }[];
  counts: Record<PipelineStageId, number>;
  raisedCaseCount: number;
  caseStages: ReadonlyMap<string, PipelineStageId | null>;
  zincStage: PipelineStageId | null;
  activeSignals: readonly SignalRead[];
  mappedTags: readonly TagMapping[];
  mappedTagCount: number;
  totalTagCount: number;
  currentExcursions: readonly ExcursionEvent[];
  excursionRows: readonly FlowExcursionRow[];
  previousShiftExcursionCount: number;
  longestHeldMinutes: number;
  diagnosisCases: readonly Case[];
  diagnosisRows: readonly { caseItem: Case; causeLabel: string }[];
  roleScreenCases: readonly Case[];
  displayCases: readonly Case[];
  openCaseRows: readonly FlowCaseRow[];
  casesStageRows: readonly FlowCaseRow[];
  actionCaseRows: readonly FlowCaseRow[];
  /** K13b: action-stage cases whose FMEA proposal waits on the Senior met seat. */
  fmeaProposalCount: number;
  capturedRows: readonly FlowCapturedRow[];
  learnedRows: readonly FlowLearnRow[];
  plantDataRows: readonly TagMapping[];
  shiftFeed: readonly ShiftFeedRow[];
  showRecoveryFilter: boolean;
  atStakePerDay: number;
  recoverablePerDay: number;
  capturedPerDay: number;
  driverTreeCountedExposurePerDay: number;
  /** K15: the investigation runs at the cursor, newest first. */
  investigationRuns: readonly InvestigationRunRow[];
  cells: Record<PipelineStageId, PipelineCellRead>;
};

export type FlowLandingReadInput = {
  state: SessionState;
  cases: readonly Case[];
  roleCases?: readonly Case[];
  actions: readonly RecommendedAction[];
  reviews: readonly FMEAUpdateReview[];
  analysisFrames: readonly AnalysisReplay[number][];
  replayByFixture?: PipelineReplays;
  stageTimelines: CaseStageTimeline;
  diagnosis: Diagnosis;
  /** Cached at shift boundaries so the replay does not read all SPC series twice per tick. */
  previousShiftExcursionCount?: number;
  tagMappings: readonly TagMapping[];
  driverTreeSeed: DriverTreeSeed;
  /**
   * The fixture the replay frames were evaluated for. A case reads those frames
   * only when its own `analysisRun.fixture` names the same one, so a second
   * fixture can never be priced with the first one's money. Required, so a new
   * caller must state which fixture its frames belong to.
   */
  analysisFixture: string | undefined;
  failureModes: readonly FailureMode[];
  /** Session approvals that turn a case into a Learn-stage arrival. */
  fmeaLearning?: FmeaLearningSessionState;
  outcomeFilter?: string | null;
  /**
   * K15: the investigation runs at the cursor (recorded + session-started),
   * listed on the Diagnosis stage and counted on its Agent chip.
   */
  investigationRuns?: readonly InvestigationRunRow[];
  /**
   * #731: false for the floor seats (Operator, Control room), whose pipeline
   * cells carry no US$ money line. Defaults to true, so every other seat and
   * every existing caller keeps the priced cell.
   */
  showMoney?: boolean;
};

function ownerLossesPerDay(
  cases: readonly Case[],
  seed: PricedDriverTree,
  cursor: SeriesTime,
): ReadonlyMap<string, number | null> {
  const ownersByCase = new Map<string, number>();
  for (const issue of seed.issues) {
    if (!issue.caseId || !issue.countsTowardTotal) continue;
    ownersByCase.set(
      issue.caseId,
      (ownersByCase.get(issue.caseId) ?? 0) + openCountedAmountPerDay(issue, seed, cursor),
    );
  }
  return new Map(cases.map((caseItem) => {
    return [caseItem.id, ownersByCase.get(caseItem.id) ?? null];
  }));
}

function linkedLossesForResolution(
  cases: readonly Case[],
  seed: PricedDriverTree,
  cursor: SeriesTime,
): ReadonlyMap<string, number | null> {
  const amountsByCase = new Map<string, number>();
  for (const issue of seed.issues) {
    if (!issue.caseId || !driverIssueResolvedAtCursor(issue, cursor)) continue;
    const amount = issue.countsTowardTotal
      ? issue.countedAmountPerDay ?? issue.impactPerDay
      : issue.sharedPathContribution?.impactPerDay ?? issue.impactPerDay;
    amountsByCase.set(issue.caseId, (amountsByCase.get(issue.caseId) ?? 0) + amount);
  }
  return new Map(cases.map((caseItem) => [caseItem.id, amountsByCase.get(caseItem.id) ?? null]));
}

const CASE_SEVERITY_RANK: Readonly<Record<Case['severity'], number>> = {
  high: 3,
  med: 2,
  low: 1,
  monitor: 0,
};

function formatCount(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('en-US');
}

function formatCurrencyAtLeastZero(value: number): string {
  return formatCurrencyStandard(Math.max(0, value));
}

function formatCaseAge(minutes: number): string {
  const days = Math.floor(minutes / (24 * 60));
  if (days >= 1) return `${days}d ${formatElapsedMinutes(minutes - days * 24 * 60)}`;
  return formatElapsedMinutes(minutes);
}

export function formatPipelineSignalRole(role: string): string {
  const label = role
    .replace(/[_-]/g, ' ')
    .replace(/\bph\b/gi, 'pH')
    // Element symbols and the grind size keep their case (`Zn rougher con Zn`, `Grind P80`).
    .replace(/\b(zn|pb|ag|cu|fe|as)\b/gi, (symbol) => `${symbol.charAt(0).toUpperCase()}${symbol.slice(1).toLowerCase()}`)
    .replace(/\bsio2\b/gi, 'SiO2')
    .replace(/\bp80\b/gi, 'P80')
    // Reagent formulas and plant acronyms read as the plant writes them
    // (polish.P28b): `Reagent ZnSO4 rate`, `Cyclone pump VSD`.
    .replace(/\b(znso4|cuso4)\b/gi, (formula) => `${formula.charAt(0).toUpperCase()}${formula.charAt(1).toLowerCase()}SO4`)
    .replace(/\b(smbs|mibc|vsd)\b/gi, (acronym) => acronym.toUpperCase())
    .replace(/\b3418a\b/gi, '3418A')
    .replace(/\baero\b/gi, 'Aero');
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

export function formatExcursionRule(rule: ExcursionEvent['rule']): string {
  const labels: Readonly<Record<ExcursionEvent['rule'], string>> = {
    'beyond-limit': 'Beyond limit',
    'six-point-trend': 'Six-point trend',
    'widening-variability': 'Widening variability',
    cyclical: 'Cyclical pattern',
  };
  return labels[rule];
}

function caseFeedLabel(caseItem: Case): string {
  if (caseItem.headline?.label) return caseItem.headline.label;
  const readingId = caseItem.affects?.readingId;
  const readingLabel = readingId ? readingByIdLookup(readingId)?.label : undefined;
  return readingLabel ?? caseItem.feedNodeLabel ?? caseItem.title;
}

function actionIsInField(
  caseId: string,
  cursor: SeriesTime,
  actions: readonly RecommendedAction[],
): boolean {
  return actions.some((action) => {
    if (action.caseId !== caseId || (action.status !== 'approved' && action.status !== 'done')) return false;
    const stamp = action.status === 'done' ? action.implementedAt : action.decision?.at;
    if (!stamp) return false;
    const at = flowStamp(stamp, cursor);
    return at !== null && at <= cursor;
  });
}

/**
 * The cases registered on the flow at the clock: raised at or before it. The
 * same rule `caseStageAt` applies to the role screen's case set, for a reader
 * that holds the whole case list (the Overview's zero-touch read, row 324).
 */
export function casesRaisedByClock(cases: readonly Case[], cursor: SeriesTime): Case[] {
  return cases.filter((caseItem) => {
    const raisedAt = flowStamp(caseItem.raisedAt, DEFAULT_CURSOR);
    return raisedAt !== null && raisedAt <= cursor;
  });
}

function roleCasesAtClock(
  cases: readonly Case[],
  cursor: SeriesTime,
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
  timelines: CaseStageTimeline,
): Case[] {
  return cases.filter((caseItem) =>
    caseStageAt(cursor, milestonesForCase(caseItem, timelines, actions, reviews)) !== null,
  );
}

function caseArrivalsThisShift(
  cases: readonly Case[],
  cursor: SeriesTime,
  timelines: CaseStageTimeline,
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
): number {
  const { start } = shiftOf(cursor);
  return cases.filter((caseItem) => {
    const timeline = milestonesForCase(caseItem, timelines, actions, reviews);
    const raisedAt = timeline.raisedAt;
    return raisedAt !== null && raisedAt >= start && raisedAt <= cursor &&
      caseStageAt(cursor, timeline) === 'cases';
  }).length;
}

function learnedRowsInRollingWeek(
  reviews: readonly FMEAUpdateReview[],
  cursor: SeriesTime,
  sessionApprovals: readonly { caseId: string; caseCode: string; failureModeId: string; updatedAt: string; updatedBy: string }[],
  failureModes: readonly FailureMode[],
): FlowLearnRow[] {
  const start = addMinutes(cursor, -7 * 24 * 60);
  const reviewRows = reviews.flatMap((review) => {
    if (review.approvalStatus !== 'approved' || !review.approvedAt) return [];
    const at = flowStamp(review.approvedAt, cursor);
    return at && at >= start && at <= cursor ? [{
      id: `review:${review.id}`,
      at,
      summary: review.proposedChange.summary,
      detail: `${review.id} · ${review.reviewedBy ?? 'reviewed'}`,
      source: 'review' as const,
      code: review.id,
      approvedBy: review.approvedBy,
    }] : [];
  });
  const approvalRows = sessionApprovals.flatMap((approval) => {
    const at = approval.updatedAt.slice(0, 16) as SeriesTime;
    if (at < start || at > cursor) return [];
    const mode = failureModes.find((row) => row.id === approval.failureModeId);
    if (!mode) return [];
    return [{
      id: `case-approval:${approval.caseId}`,
      at,
      summary: mode.failureMode,
      detail: `${approval.caseCode} · ${approval.updatedBy}`,
      source: 'case-approval' as const,
      approvalCaseId: approval.caseId,
      code: approval.caseCode,
      approvedBy: approval.updatedBy,
    }];
  });
  return [...reviewRows, ...approvalRows].sort((left, right) => right.at.localeCompare(left.at));
}

function milestoneArrivals(
  cases: readonly Case[],
  cursor: SeriesTime,
  start: SeriesTime,
  timelines: CaseStageTimeline,
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
  milestone: 'valueAt' | 'learnAt',
): { shift: number; total: number } {
  let shift = 0;
  let total = 0;
  for (const caseItem of cases) {
    const at = milestonesForCase(caseItem, timelines, actions, reviews)[milestone];
    if (!at || at > cursor) continue;
    total += 1;
    if (at >= start) shift += 1;
  }
  return { shift, total };
}

function caseShiftFeed(
  cases: readonly Case[],
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
  frames: readonly AnalysisReplay[number][],
  cursor: SeriesTime,
  timelines: CaseStageTimeline,
  replayByFixture?: PipelineReplays,
): ShiftFeedRow[] {
  const events: ShiftFeedRow[] = [];
  const add = (at: SeriesTime | null | undefined, actor: ShiftFeedRow['actor'], detail: string, caseId: string) => {
    if (!at || at > cursor) return;
    events.push({ at, timeLabel: `${formatDayMonth(at)} ${at.slice(11, 16)}`, actor, detail, caseId, repeatCount: 1 });
  };
  const addForCase = (
    caseItem: Case,
    at: SeriesTime | null | undefined,
    actor: ShiftFeedRow['actor'],
    detail: string,
  ) => {
    const raisedAt = milestonesForCase(caseItem, timelines, actions, reviews).raisedAt;
    if (!raisedAt || !at || at < raisedAt) return;
    add(at, actor, detail, caseItem.id);
  };

  for (const caseItem of cases) {
    const timeline = milestonesForCase(caseItem, timelines, actions, reviews);
    if (!timeline.raisedAt || timeline.raisedAt > cursor) continue;
    const label = caseItem.code ?? caseItem.title;
    addForCase(caseItem, timeline.raisedAt, 'Engine', `${label} case raised`);
    if (caseItem.diagnosedAt) {
      addForCase(caseItem, flowStamp(caseItem.diagnosedAt, cursor), 'Agent', `${label} diagnosis ready`);
    }
    if (caseItem.handler?.since) {
      addForCase(caseItem, flowStamp(caseItem.handler.since, cursor), 'Human', `${caseItem.handler.name} took the case`);
    }
  }

  for (const action of actions) {
    const item = cases.find((caseItem) => caseItem.id === action.caseId);
    if (!item) continue;
    const label = item.code ?? item.title;
    if (action.proposedAt) addForCase(item, flowStamp(action.proposedAt, cursor), 'Agent', `${label} · action proposed`);
    if (action.decision?.at) {
      const decision = action.status === 'rejected' ? 'declined' : 'approved';
      addForCase(item, flowStamp(action.decision.at, cursor), 'Human', `${label} · action ${decision}`);
    }
    if (action.status === 'done') addForCase(item, flowStamp(action.implementedAt, cursor), 'Human', `${label} · action in the field`);
  }

  const outcomeIdToCase = new Map(CASE_OUTCOMES.map((outcome) => [outcome.id, outcome.caseId]));
  for (const review of reviews) {
    if (!review.caseOutcomeId) continue;
    const outcomeCaseId = outcomeIdToCase.get(review.caseOutcomeId);
    const caseItem = cases.find((row) => row.id === outcomeCaseId);
    if (!caseItem) continue;
    const label = caseItem.code ?? caseItem.title;
    if (review.reviewedAt) addForCase(caseItem, flowStamp(review.reviewedAt, cursor), 'Human', `${label} · FMEA reviewed`);
    if (review.approvalStatus === 'approved' && review.approvedAt) {
      addForCase(caseItem, flowStamp(review.approvedAt, cursor), 'Human', `${label} · FMEA row approved`);
    }
  }

  for (const item of cases.filter((row) => row.analysisRun)) {
  for (const frame of caseReplayFrames(item, replayByFixture ?? frames)) {
    const at = frame.at.slice(0, 16) as SeriesTime;
    if (at > cursor) break;
    for (const signal of frame.result.spc_signals ?? []) {
      if (!signal.fired || !signal.first_event_time) continue;
      const eventAt = signal.first_event_time.slice(0, 16) as SeriesTime;
      addForCase(item, eventAt, 'Engine', `${formatPipelineSignalRole(signal.signal_role)} SPC signal fired`);
    }
  }
  }

  const deduped = [...new Map(events.map((event) => [`${event.at}|${event.actor}|${event.detail}`, event])).values()];
  const collapsed = new Map<string, ShiftFeedRow>();
  for (const event of deduped) {
    const key = `${event.caseId}|${event.actor}|${event.detail}`;
    const prior = collapsed.get(key);
    collapsed.set(key, prior
      ? { ...prior, at: event.at > prior.at ? event.at : prior.at, timeLabel: event.at > prior.at ? event.timeLabel : prior.timeLabel, repeatCount: prior.repeatCount + 1 }
      : event);
  }
  const sorted = [...collapsed.values()].sort((a, b) => b.at.localeCompare(a.at));
  const representedActors = new Map<ShiftFeedRow['actor'], ShiftFeedRow>();
  for (const actor of ['Engine', 'Agent', 'Human'] as const) {
    const event = sorted.find((row) => row.actor === actor);
    if (event) representedActors.set(actor, event);
  }
  const visible = new Map([...sorted.slice(0, 4), ...representedActors.values()]
    .map((event) => [`${event.at}|${event.actor}|${event.detail}`, event]));
  return [...visible.values()].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 8);
}

/**
 * One basis (D17 / K7): every priced case row carries its own counted share —
 * the same US$/day the driver tree and the case page's Value cell show. A case
 * whose issue is an owner prices on that owner's open counted amount; a case
 * whose issue is a shared-path contributor (the lime silo inside the Zn
 * recovery owner) prices on its share. The contributor share already sits
 * inside its owner's counted amount, so it never joins the Cases cell totals;
 * it prices this row so the row can never outrank the owner it feeds.
 *
 * The contributor's owner case code comes back beside the amounts so the row
 * can say the share is contained, and its money cell can read as part of the
 * owner rather than as a second addition.
 */
function countedCaseAmounts(
  cases: readonly Case[],
  seed: PricedDriverTree,
  cursor: SeriesTime,
): { amounts: ReadonlyMap<string, number | null>; containedWithin: ReadonlyMap<string, string> } {
  const amounts = new Map<string, number>();
  const containedWithin = new Map<string, string>();
  const caseById = new Map(cases.map((caseItem) => [caseItem.id, caseItem]));
  for (const issue of seed.issues) {
    if (!issue.caseId) continue;
    if (issue.countsTowardTotal) {
      amounts.set(
        issue.caseId,
        (amounts.get(issue.caseId) ?? 0) + openCountedAmountPerDay(issue, seed, cursor),
      );
      continue;
    }
    if (!issue.sharedPathContribution || driverIssueResolvedAtCursor(issue, cursor)) continue;
    amounts.set(
      issue.caseId,
      (amounts.get(issue.caseId) ?? 0) + issue.sharedPathContribution.impactPerDay,
    );
    const owner = seed.issues.find((row) => row.id === issue.sharedPathContribution!.ownerIssueId);
    const ownerCode = owner?.caseId
      ? caseById.get(owner.caseId)?.code ?? owner.caseId
      : owner?.id;
    if (ownerCode) containedWithin.set(issue.caseId, ownerCode);
  }
  return {
    amounts: new Map(cases.map((caseItem) => [caseItem.id, amounts.get(caseItem.id) ?? null])),
    containedWithin,
  };
}

/**
 * The recorded run's recovery comparison for the cases that name the frames'
 * fixture. The run estimate itself stays on the case page; this row prices on
 * the counted share, so the frames supply the comparison only. A non-USD
 * envelope throws rather than dressing a second deck as US$.
 */
function analysisCaseValues(
  cases: readonly Case[],
  frames: readonly AnalysisReplay[number][],
  cursor: SeriesTime,
  analysisFixture: string | undefined,
  replayByFixture?: PipelineReplays,
): ReadonlyMap<string, AnalysisCaseValue> {
  const values = new Map<string, AnalysisCaseValue>();
  if (!analysisFixture && !replayByFixture) return values;
  for (const caseItem of cases) {
    const ownFrames = replayByFixture ? caseReplayFrames(caseItem, replayByFixture) : caseItem.analysisRun?.fixture === analysisFixture ? frames : [];
    const ownFrame = analysisFrameAtClock(ownFrames, cursor);
    const stop = stopPairRead(ownFrame?.result);
    if (stop) {
      // D16/comp: a one-off is a kind, not a daily rank — the money line reads
      // "one-off" and the stop pair sits under it as secondary text.
      values.set(caseItem.id, { money: 'one-off', comparison: `${stop.money} · conditional`, oneOff: true });
      continue;
    }
    // The v1 stop request includes a dormant recovery model; never inspect it.
    if (caseItem.stopPlan || !ownFrame) continue;
    const impact = ownFrame.result.financial_impact;
    if (impact) assertUsd(impact.currency);
    const expectation = ownFrame.result.expectations?.find((row) => row.signal_role === caseItem.analysisRun?.expectationRole);
    const comparison = expectation
      ? `rougher recovery ${oneDecimalPercent(expectation.observed)}% vs ${oneDecimalPercent(expectation.expected)}% expected · ${oneDecimalPercent(expectation.shortfall)} pt short`
      : 'recorded run estimate';
    const at = caseItem.diagnosedAt ? flowStamp(caseItem.diagnosedAt, cursor) : null;
    if (at === null || at > cursor) continue;
    values.set(caseItem.id, { comparison });
  }
  return values;
}

function openCaseRows(
  cases: readonly Case[],
  cursor: SeriesTime,
  actions: readonly RecommendedAction[],
  reviews: readonly FMEAUpdateReview[],
  timelines: CaseStageTimeline,
  losses: ReadonlyMap<string, number | null>,
  analysisValues: ReadonlyMap<string, AnalysisCaseValue>,
  containedWithin: ReadonlyMap<string, string>,
  approvedStopIds: ReadonlySet<string> = new Set(),
  /**
   * K13b: cases whose action is approved and whose FMEA change is a draft
   * proposal waiting on the Senior met seat. They leave "awaiting approval".
   */
  fmeaProposalCaseIds: ReadonlySet<string> = new Set(),
  /**
   * K13b r2: the landing's session-aware stage per case (the same map the strip
   * counts use, with the approved case's learnAt override applied). The body
   * reads it so a live-approved case leaves Action for Value exactly when the
   * strip says it does — otherwise the cell and its body disagree.
   */
  stageByCase?: ReadonlyMap<string, PipelineStageId | null>,
): FlowCaseRow[] {
  const active = new Set<PipelineStageId>(['cases', 'diagnosis', 'action']);
  // K7 r4: the containment marker is only honest while the owner case it names
  // is itself an open row on this landing at this clock. Resolve the open set
  // once so a contributor row can check its owner before it prices.
  const opened = cases.flatMap((caseItem) => {
    const timeline = milestonesForCase(caseItem, timelines, actions, reviews);
    const stage = stageByCase?.get(caseItem.id) ?? caseStageAt(cursor, timeline);
    return stage && active.has(stage) ? [{ caseItem, timeline, stage }] : [];
  });
  const openCodes = new Set(opened.map(({ caseItem }) => caseItem.code ?? caseItem.id));
  return opened.flatMap(({ caseItem, timeline, stage }) => {
    const raisedAt = timeline.raisedAt!;
    const actorRole = caseItem.handler?.role ?? caseItem.assignedTo;
    const handlerName = caseItem.handler?.name?.trim();
    const roleNamedHandler = handlerName && (
      actorRole === 'metallurgist' && /\bmetallurgist\b/i.test(handlerName) ||
      actorRole === 'operator' && /\boperator\b/i.test(handlerName) ||
      actorRole === 'maintenance' && /\bmaintenance\b/i.test(handlerName)
    );
    const actor = handlerName
      ? roleNamedHandler ? handlerName : `${handlerName} · ${roleShortLabel(actorRole)}`
      : roleShortLabel(actorRole);
    // D17: the row prices and ranks on the counted share, never the recorded
    // run's envelope, so the contributor case sorts below its owner. The run
    // estimate's recovery comparison stays on the case page; this row names the
    // owner the share sits inside, so the Cases and Action cells reconcile by
    // reading and the row stays at most two lines.
    // K7 r4: before the owner case is raised (06:50 for ZN-REC) there is no
    // owner row to sit inside, so the contributor renders exactly as it did
    // before K7 — unpriced, `Feeds <node>`, ranked among the unpriced rows.
    const ownerCode = containedWithin.get(caseItem.id);
    const ownerOpen = ownerCode !== undefined && openCodes.has(ownerCode);
    const loss = ownerCode && !ownerOpen ? null : losses.get(caseItem.id) ?? null;
    // The containment marker rides with a priced share only, so a row can never
    // name an owner it does not actually price inside.
    const containedOwner = ownerOpen && loss !== null ? ownerCode : undefined;
    const analysisValue = containedOwner ? undefined : analysisValues.get(caseItem.id);
    const inField = stage === 'action' && actionIsInField(caseItem.id, cursor, actions);
    const feedLabel = caseFeedLabel(caseItem);
    return [{
      caseItem,
      stage,
      stageIndex: Math.max(0, PIPELINE_STAGES.findIndex((row) => row.id === stage)),
      age: formatCaseAge(Math.max(0, diffMinutes(cursor, raisedAt))),
      actor,
      dailyLossPerDay: loss,
      money: analysisValue?.money ?? (loss === null ? null : `${formatCurrencyExact(loss)}/day`),
      moneyComparison: analysisValue?.oneOff ? analysisValue.comparison : loss === null
        ? `feeds ${feedLabel}`
        : containedOwner
          ? `counted share · within ${containedOwner}`
          : analysisValue
            ? `counted share · ${analysisValue.comparison} · feeds ${feedLabel}`
            : stage === 'action' ? `recoverable · feeds ${feedLabel}` : `at risk · feeds ${feedLabel}`,
      feedLabel,
      // K13b: after the action gate the case sits with the Senior met seat, not
      // back in the approval queue. A planned stop keeps its own read first.
      actionStatus: approvedStopIds.has(caseItem.id)
        ? 'stop planned' as const
        : fmeaProposalCaseIds.has(caseItem.id)
          ? 'FMEA proposal · Senior met' as const
          : inField ? 'in the field' as const : 'awaiting approval' as const,
      ...(containedOwner ? { containedWithin: containedOwner } : {}),
    }];
  }).sort((a, b) => {
    if (a.dailyLossPerDay !== null && b.dailyLossPerDay === null) return -1;
    if (a.dailyLossPerDay === null && b.dailyLossPerDay !== null) return 1;
    if (a.dailyLossPerDay !== null && b.dailyLossPerDay !== null && a.dailyLossPerDay !== b.dailyLossPerDay) {
      return b.dailyLossPerDay - a.dailyLossPerDay;
    }
    return CASE_SEVERITY_RANK[b.caseItem.severity] - CASE_SEVERITY_RANK[a.caseItem.severity] ||
      a.caseItem.title.localeCompare(b.caseItem.title);
  });
}

function cellReads(input: {
  counts: Record<PipelineStageId, number>;
  totalTagCount: number;
  activeSignalCount: number;
  priorShiftExcursionCount: number;
  longestHeldMinutes: number;
  newCaseCount: number;
  zeroTouchCount: number;
  awaitingApprovalCount: number;
  inFieldCount: number;
  plannedStopCount: number;
  /** K13b: action-approved cases whose FMEA change waits on the Senior met seat. */
  fmeaProposalCount: number;
  valueShiftCount: number;
  valueToDateCount: number;
  learnWeekCount: number;
  atStakePerDay: number;
  recoverablePerDay: number;
  capturedPerDay: number;
  /** K15: the investigation runs the Diagnosis stage counts on its Agent chip. */
  investigationRunCount: number;
  /** #731: a floor seat (Operator, Control room) reads no US$ in a strip cell. */
  showMoney: boolean;
}): Record<PipelineStageId, PipelineCellRead> {
  const n = input.counts;
  // #731: a floor seat keeps the cell's count and its non-money comparison; the
  // money line and its comparison are not rendered at all (never an empty slot,
  // never a US$ the seat cannot act on).
  const money = input.showMoney;
  return {
    'plant-data': {
      count: formatCount(n['plant-data']),
      comparison: 'mapped against the plant I/O list',
    },
    signals: {
      count: formatCount(input.activeSignalCount),
      comparison: 'active SPC signals',
    },
    excursions: {
      count: formatCount(n.excursions),
      comparison: `${formatCount(input.priorShiftExcursionCount)} previous shift · held ${formatElapsedMinutes(input.longestHeldMinutes)}`,
    },
    cases: {
      count: formatCount(n.cases),
      comparison: `open · ${formatCount(input.newCaseCount)} new since shift start`,
      ...(money ? {
        money: `${formatCurrencyAtLeastZero(input.atStakePerDay)}/day at stake`,
        moneyComparison: `${formatCurrencyAtLeastZero(input.recoverablePerDay)}/day recoverable downstream`,
      } : {}),
    },
    diagnosis: {
      count: formatCount(n.diagnosis),
      comparison: `ranked · ${formatCount(input.zeroTouchCount)} zero-touch · no diagnosis`,
      // K15 / D24: the Diagnosis stage's Agent chip carries the investigation
      // run count, so the run is counted where the workflow sits.
      actorCounts: {
        Agent: `Agent · ${formatCount(input.investigationRunCount)} run${input.investigationRunCount === 1 ? '' : 's'}`,
      },
    },
    action: {
      count: formatCount(n.action),
      comparison: `${formatCount(input.awaitingApprovalCount)} awaiting approval · ${formatCount(input.inFieldCount)} in the field${input.plannedStopCount ? ` · ${formatCount(input.plannedStopCount)} stop planned` : ''}${input.fmeaProposalCount ? ` · ${formatCount(input.fmeaProposalCount)} FMEA proposal · Senior met` : ''}`,
      ...(money ? {
        money: `${formatCurrencyAtLeastZero(input.recoverablePerDay)}/day recoverable`,
        moneyComparison: `vs ${formatCurrencyAtLeastZero(input.atStakePerDay)}/day at stake`,
      } : {}),
    },
    value: {
      count: formatCount(input.valueShiftCount),
      countContext: 'this shift',
      comparison: `${formatCount(input.valueToDateCount)} to date`,
      ...(money ? {
        money: `${formatCurrencyAtLeastZero(input.capturedPerDay)}/day captured`,
        moneyComparison: `vs ${formatCurrencyAtLeastZero(input.atStakePerDay)}/day at stake`,
      } : {}),
    },
    learn: {
      count: formatCount(input.learnWeekCount),
      comparison: 'FMEA rows updated in the past 7 days',
    },
  };
}

export type FlowExcursionRow = { event: ExcursionEvent; instrumentTag: string };

/** A recorded fixture case's fired signal at the cursor (one row per case). */
export type FlowFixtureSignal = {
  caseId: string;
  tag: string;
  count: number;
  rules: string;
  /** The same rule labels as `rules`, one per fired signal. */
  ruleLabels: readonly string[];
  mapping: TagMapping;
};

/**
 * A recorded fixture case's active excursion at the cursor. `since` / `held`
 * are the display strings of `sinceAt` / `heldMinutes`.
 */
export type FlowFixtureExcursion = {
  id: string;
  tag: string;
  rule: string;
  held: string;
  since: string;
  sinceAt: SeriesTime;
  heldMinutes: number;
  mapping?: TagMapping;
};

type FixtureFrameSignal = NonNullable<AnalysisReplay[number]['result']['spc_signals']>[number];
type FixtureFrameExcursion = NonNullable<AnalysisReplay[number]['result']['excursions']>[number];

/**
 * polish.P28a (adjudication 8): a fixture excursion's start and held time are
 * read from the reading's own series against the cursor, on the same basis as
 * the SPC excursions beside it (first event → latest sample at the cursor).
 * A band-limit rule starts where the series last crossed its band (the run of
 * samples beyond it that reaches the latest sample), which is where the case
 * page's excursion starts too; any other rule starts at the engine's first
 * event. The frame's own `held_minutes` is not read.
 */
export function fixtureExcursionSpan(
  event: Pick<FixtureFrameExcursion, 'first_event_time'>,
  signal: Pick<FixtureFrameSignal, 'limits'> | undefined,
  mapping: TagMapping | undefined,
  state: SessionState,
): { sinceAt: SeriesTime; heldMinutes: number } {
  const firstEvent = event.first_event_time.slice(0, 16) as SeriesTime;
  const samples = mapping?.seriesId
    ? seriesAt(mapping.seriesId, state).points.filter(
      (point): point is { at: SeriesTime; value: number } => point.value !== null,
    )
    : [];
  const latest = samples.at(-1)?.at ?? state.cursor;
  const bandHigh = signal?.limits?.band_high ?? null;
  const bandLow = signal?.limits?.band_low ?? null;
  let sinceAt = firstEvent;
  if (bandHigh !== null || bandLow !== null) {
    const beyond = (value: number) =>
      (bandHigh !== null && value > bandHigh) || (bandLow !== null && value < bandLow);
    let runStart: SeriesTime | undefined;
    for (let index = samples.length - 1; index >= 0 && beyond(samples[index]!.value); index -= 1) {
      runStart = samples[index]!.at;
    }
    if (runStart && runStart < sinceAt) sinceAt = runStart;
  }
  return { sinceAt, heldMinutes: Math.max(0, diffMinutes(latest, sinceAt)) };
}

export function excursionPreview(
  events: readonly ExcursionEvent[],
  mappings: readonly TagMapping[],
  limit = events.length,
): FlowExcursionRow[] {
  return events.slice(0, Math.max(0, limit)).map((event) => ({
    event,
    instrumentTag: mappings.find((row) => row.readingId === event.readingId)?.instrumentTag ?? event.readingId,
  }));
}

export function plantDataPreview(rows: readonly TagMapping[], limit = rows.length): readonly TagMapping[] {
  return rows.slice(0, Math.max(0, limit));
}

export function heldMinutesForExcursions(
  events: readonly { detectedAt: SeriesTime; points: readonly SeriesTime[] }[],
): number {
  return events.reduce((longest, event) => {
    const latest = event.points.at(-1) ?? event.detectedAt;
    return Math.max(longest, Math.max(0, diffMinutes(latest, event.detectedAt)));
  }, 0);
}

/** Complete, pure projection for the eight-stage landing and its Cases body. */
/** What a case's counted stage reads from: the Advisory strip's case basis. */
export type FlowCaseStageInput = Pick<FlowLandingReadInput, 'cases' | 'actions' | 'reviews' | 'stageTimelines' | 'fmeaLearning'>;

/**
 * Each case's stage timetable exactly as the strip counts it — the seeded
 * milestones with the session's FMEA approval pulling Learn forward. One
 * function, so the strip, the rail badge (`advisoryRailBadge`) and any surface
 * that counts open cases at another instant (the Workflows catalogue's
 * Priority actions runs, K19) read one basis.
 */
export function flowCaseTimelines(input: FlowCaseStageInput): Map<string, CaseStageMilestones> {
  const sessionApprovalAtByCase = new Map(
    [...(input.fmeaLearning?.approvals.values() ?? [])]
      .map((approval) => [approval.caseId, approval.updatedAt.slice(0, 16) as SeriesTime]),
  );
  const timelines = new Map<string, CaseStageMilestones>();
  for (const caseItem of input.cases) {
    const baseTimeline = milestonesForCase(caseItem, input.stageTimelines, input.actions, input.reviews);
    const sessionLearnAt = sessionApprovalAtByCase.get(caseItem.id);
    timelines.set(caseItem.id, sessionLearnAt
      ? { ...baseTimeline, learnAt: baseTimeline.learnAt && baseTimeline.learnAt < sessionLearnAt ? baseTimeline.learnAt : sessionLearnAt }
      : baseTimeline);
  }
  return timelines;
}

export function flowLandingRead(input: FlowLandingReadInput): FlowLandingRead {
  const { state, cases, actions, reviews, diagnosis, stageTimelines } = input;
  const driverTreeSeed = pricedDriverTree(input.driverTreeSeed, state);
  const cursor = state.cursor;
  const shift = shiftOf(cursor);
  const timelines = flowCaseTimelines(input);
  const caseStages = new Map<string, PipelineStageId | null>();
  /** K13b r2: the same session-aware stage the strip counts use, for the body. */
  const stageByCase = new Map<string, PipelineStageId | null>();
  const fixtureCases = cases.filter((row) => row.analysisRun?.milestonesFromFrames && input.replayByFixture?.[row.analysisRun.fixture]);
  const fixtureVariableIds = new Set(fixtureCases.map(variableIdForCase));
  const legacySignals = diagnosis.signals.filter((signal) => !fixtureVariableIds.has(`mv-${signal.mapping.seriesId?.replace(/^rs-/, '')}`));
  const fixtureSignals: FlowFixtureSignal[] = fixtureCases.flatMap((caseItem) => {
    const frame = analysisFrameAtClock(caseReplayFrames(caseItem, input.replayByFixture ?? {}), cursor);
    const fired = frame?.result.spc_signals?.filter((signal) => signal.fired) ?? [];
    const mapping = input.tagMappings.find((row) => `mv-${row.seriesId?.replace(/^rs-/, '')}` === variableIdForCase(caseItem));
    if (!fired.length || !mapping) return [];
    const ruleLabels = fired.map((signal) => ruleLabel(signal.rule_id, readingByIdLookup(mapping.readingId)?.unit ?? ''));
    return [{ caseId: caseItem.id, tag: mapping.instrumentTag, count: fired.length, rules: ruleLabels.join(' · '), ruleLabels, mapping }];
  });
  const fixtureExcursions: FlowFixtureExcursion[] = fixtureCases.flatMap((caseItem) => {
    const frame = analysisFrameAtClock(caseReplayFrames(caseItem, input.replayByFixture ?? {}), cursor);
    const mapping = input.tagMappings.find((row) => `mv-${row.seriesId?.replace(/^rs-/, '')}` === variableIdForCase(caseItem));
    return (frame?.result.excursions ?? []).filter((event) => event.state === 'active').map((event) => {
      const signal = frame?.result.spc_signals?.find((row) => row.fired && event.rule_ids.includes(row.rule_id));
      const { sinceAt, heldMinutes } = fixtureExcursionSpan(event, signal, mapping, state);
      return {
        id: event.excursion_id, tag: mapping?.instrumentTag ?? event.signal_role,
        rule: ruleLabel(event.rule_ids[0] ?? '', mapping ? readingByIdLookup(mapping.readingId)?.unit ?? '' : ''),
        held: formatElapsedMinutes(heldMinutes),
        since: `${formatDayMonth(sinceAt)} ${sinceAt.slice(11, 16)}`,
        sinceAt,
        heldMinutes,
        mapping,
      };
    });
  });
  const counts: Record<PipelineStageId, number> = {
    'plant-data': input.tagMappings.filter((row) => row.seriesId !== undefined).length,
    signals: legacySignals.filter((signal) => (signal.spc?.events.length ?? 0) > 0).length + fixtureSignals.length,
    excursions: 0,
    cases: 0,
    diagnosis: 0,
    action: 0,
    value: 0,
    learn: 0,
  };

  for (const caseItem of cases) {
    const timeline = timelines.get(caseItem.id)!;
    const stage = caseStageAt(cursor, timeline);
    const markerStage = zincMarkerStageAt(cursor, timeline) ?? stage;
    stageByCase.set(caseItem.id, stage);
    caseStages.set(caseItem.id, markerStage);
    if (stage && CASE_STAGE_ORDER.includes(stage)) counts[stage] += 1;
  }

  const currentExcursions = [...new Map(
    legacySignals.flatMap((signal) => signal.events).map((event) => [event.id, event]),
  ).values()];
  counts.excursions = currentExcursions.length + fixtureExcursions.length;
  const priorCursor = addMinutes(shift.start, -1);
  const previousShiftExcursionCount = input.previousShiftExcursionCount ??
    excursions({ ...state, cursor: priorCursor }).length;
  const activeSignals = legacySignals.filter((signal) => (signal.spc?.events.length ?? 0) > 0);
  const displayCases = input.outcomeFilter === 'recovery'
    ? recoveryCasesForFlow(cases, diagnosis)
    : [...cases];
  const roleCases = input.roleCases ?? cases;
  const recoveryRoleCases = input.outcomeFilter === 'recovery'
    ? recoveryCasesForFlow(roleCases, diagnosis)
    : roleCases;
  const roleScreenCases = roleCasesAtClock(recoveryRoleCases, cursor, actions, reviews, stageTimelines);
  const diagnosisCases = cases.filter((caseItem) => caseStages.get(caseItem.id) === 'diagnosis');
  const diagnosisRows = diagnosisCases.map((caseItem) => {
    const referencedMode = input.failureModes.find((mode) => caseItem.causeCandidate.includes(mode.id));
    return {
      caseItem,
      causeLabel: referencedMode
        ? caseItem.causeCandidate.split(referencedMode.id).join(referencedMode.failureMode)
        : caseItem.causeCandidate,
    };
  });
  const raisedCaseCount = cases.filter((caseItem) => {
    const raisedAt = milestonesForCase(caseItem, stageTimelines, actions, reviews).raisedAt;
    return raisedAt !== null && raisedAt <= cursor;
  }).length;
  const ownerLosses = ownerLossesPerDay(cases, driverTreeSeed, cursor);
  const resolutionLosses = linkedLossesForResolution(cases, driverTreeSeed, cursor);
  const capturedRows: FlowCapturedRow[] = cases.flatMap((caseItem) => {
    const at = milestonesForCase(caseItem, stageTimelines, actions, reviews).valueAt;
    return at && at >= shift.start && at <= cursor
      ? [{ caseItem, at, dailyLossPerDay: resolutionLosses.get(caseItem.id) ?? null, feedLabel: caseFeedLabel(caseItem) }]
      : [];
  });
  const learnedRows = learnedRowsInRollingWeek(
    reviews,
    cursor,
    [...(input.fmeaLearning?.approvals.values() ?? [])],
    input.failureModes,
  );
  const plantDataRows = input.tagMappings.filter((row) => row.seriesId !== undefined);
  let recoverablePerDay = 0;
  const capturedPerDay = capturedRows.reduce(
    (sum, row) => sum + (row.dailyLossPerDay ?? 0),
    0,
  );
  // D20: one exposure figure, open cases only. The at-stake headline is the
  // shared open-case exposure function's total, not a second sum of case rows;
  // the flow landing knows which cases are raised and open at the cursor, so it
  // supplies that predicate.
  const openCaseIds = new Set(cases
    .filter((caseItem) => {
      const stage = caseStages.get(caseItem.id);
      return stage === 'cases' || stage === 'diagnosis' || stage === 'action';
    })
    .map((caseItem) => caseItem.id));
  for (const caseItem of cases) {
    const dailyLoss = ownerLosses.get(caseItem.id);
    if (dailyLoss === null || dailyLoss === undefined) continue;
    if (caseStages.get(caseItem.id) === 'action') recoverablePerDay += dailyLoss;
  }
  const atStakePerDay = openCaseExposurePerDay(
    driverTreeSeed,
    cursor,
    (caseId) => openCaseIds.has(caseId),
  ).total;

  const valueArrivals = milestoneArrivals(cases, cursor, shift.start, stageTimelines, actions, reviews, 'valueAt');
  const analysisValues = analysisCaseValues(cases, input.analysisFrames, cursor, input.analysisFixture, input.replayByFixture);
  const caseAmounts = countedCaseAmounts(cases, driverTreeSeed, cursor);
  const approvedStopIds = new Set(cases.filter((item) => item.stopPlan &&
    (input.fmeaLearning?.proposals.has(item.id) || input.fmeaLearning?.approvals.has(item.id))).map((item) => item.id));
  // K13b: read the case's FMEA phase, not a new engine state. A proposal waiting
  // on the Senior met seat takes the case out of "awaiting approval".
  const fmeaProposalCaseIds = new Set(cases
    .filter((item) => fmeaCasePhase(input.fmeaLearning, item.id) === 'proposed')
    .map((item) => item.id));
  const caseRows = openCaseRows(cases, cursor, actions, reviews, stageTimelines, caseAmounts.amounts, analysisValues, caseAmounts.containedWithin, approvedStopIds, fmeaProposalCaseIds, stageByCase);
  // One-off decisions have no daily rank. Place them beside the recorded case,
  // leaving the relative order of every daily-priced row untouched.
  const stopRows = caseRows.filter((row) => analysisValues.get(row.caseItem.id)?.oneOff);
  for (const row of stopRows) caseRows.splice(caseRows.indexOf(row), 1);
  const recordingIndex = caseRows.findIndex((row) => row.caseItem.analysisRun?.recordedAgent);
  caseRows.splice(recordingIndex < 0 ? caseRows.length : recordingIndex + 1, 0, ...stopRows);
  const newCaseCount = caseArrivalsThisShift(cases, cursor, stageTimelines, actions, reviews);
  const zeroTouchCount = diagnosisCases.filter((caseItem) => caseItem.branch === 'zero-touch').length;
  // K13b r2: every Action-cell bucket is a count of the body rows' own
  // `actionStatus`, using the row's precedence (stop planned before FMEA
  // proposal), so the cell and the body can never disagree. A case in both the
  // stop-planned and the FMEA-proposal set counts once, in the stop bucket.
  const actionRows = caseRows.filter((row) => row.stage === 'action');
  const bucketCount = (status: FlowCaseRow['actionStatus']) =>
    actionRows.filter((row) => row.actionStatus === status).length;
  const awaitingApprovalCount = bucketCount('awaiting approval');
  const inFieldCount = bucketCount('in the field');
  const plannedStopCount = bucketCount('stop planned');
  // K13b: an action-approved case whose FMEA change waits on the Senior met
  // seat leaves "awaiting approval" for its own bucket; the second gate empties
  // it and the case moves to Value.
  const fmeaProposalCount = bucketCount('FMEA proposal · Senior met');
  const driverTreeCountedExposurePerDay = totalExposurePerDay(driverTreeSeed);
  const cells = cellReads({
    counts,
    totalTagCount: input.tagMappings.length,
    activeSignalCount: counts.signals,
    priorShiftExcursionCount: previousShiftExcursionCount,
    longestHeldMinutes: heldMinutesForExcursions(currentExcursions),
    newCaseCount,
    zeroTouchCount,
    awaitingApprovalCount,
    inFieldCount,
    plannedStopCount,
    fmeaProposalCount,
    valueShiftCount: valueArrivals.shift,
    valueToDateCount: valueArrivals.total,
    learnWeekCount: learnedRows.length,
    atStakePerDay,
    recoverablePerDay,
    capturedPerDay,
    investigationRunCount: input.investigationRuns?.length ?? 0,
    showMoney: input.showMoney ?? true,
  });
  const shiftFeed = caseShiftFeed(cases, actions, reviews, input.analysisFrames, cursor, stageTimelines, input.replayByFixture);
  const markers = cases.flatMap((caseItem) => {
    const stage = caseStages.get(caseItem.id);
    return caseItem.analysisRun && stage ? [{ caseId: caseItem.id, label: caseItem.code ?? caseItem.id, stage, offset: 0, stack: 0 }] : [];
  });
  // Cases that share a stage stack in that cell's marker lane, first one on the
  // lane (as before), the rest beneath it — never straddling a neighbour.
  for (const marker of markers) {
    const peers = markers.filter((row) => row.stage === marker.stage);
    marker.stack = peers.indexOf(marker);
  }

  return {
    fixtureSignals,
    fixtureExcursions,
    markers,
    counts,
    raisedCaseCount,
    caseStages,
    zincStage: caseStages.get(ZINC_REPLAY_CASE_ID) ?? null,
    activeSignals,
    mappedTags: input.tagMappings,
    mappedTagCount: counts['plant-data'],
    totalTagCount: input.tagMappings.length,
    currentExcursions,
    excursionRows: excursionPreview(currentExcursions, input.tagMappings),
    previousShiftExcursionCount,
    longestHeldMinutes: heldMinutesForExcursions(currentExcursions),
    diagnosisCases,
    diagnosisRows,
    roleScreenCases,
    displayCases,
    openCaseRows: input.outcomeFilter === 'recovery'
      ? caseRows.filter((row) => displayCases.some((item) => item.id === row.caseItem.id))
      : caseRows,
    casesStageRows: caseRows.filter((row) => row.stage === 'cases'),
    actionCaseRows: actionRows,
    fmeaProposalCount,
    capturedRows,
    learnedRows,
    plantDataRows,
    shiftFeed,
    showRecoveryFilter: input.outcomeFilter === 'recovery',
    atStakePerDay,
    recoverablePerDay,
    capturedPerDay,
    driverTreeCountedExposurePerDay,
    investigationRuns: input.investigationRuns ?? [],
    cells,
  };
}
